import { ForwardedRef, forwardRef } from 'react';

import Avatar, { AvatarType, Size } from '@rover/components/src/Avatar/Avatar';
import CarouselLeft from '@rover/icons/svg/carousel-left.svg';
import Close from '@rover/icons/svg/close.svg';
import { Button, Flex, FlexProps, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';

import type { TrainerReviewsInfo } from './FullMixedReviewsList';

type CloseButtonProps = {
  display: FlexProps['display'];
  onClickClose: () => void;
  variant: 'desktop' | 'mobile';
};
const CloseButton = ({
  display,
  onClickClose,
  variant = 'desktop',
}: CloseButtonProps): JSX.Element => {
  const theme = useTheme();

  return (
    <Button
      display={display}
      variant="flat"
      width={theme.space['8x']}
      height={theme.space['8x']}
      p="0x"
      aria-label="Close reviews"
      sx={{
        '&:hover': { background: 'none' },
        '& > div > span': { display: 'flex' },
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClickClose}
    >
      {variant === 'desktop' && <Close width={theme.space['4x']} height={theme.space['4x']} />}
      {variant === 'mobile' && (
        <CarouselLeft width={theme.space['4x']} height={theme.space['4x']} />
      )}
    </Button>
  );
};

type StickyHeaderProps = {
  trainerInfo: TrainerReviewsInfo;
  onClickClose: () => void;
  isStuck: boolean;
};
function StickyHeader(
  { trainerInfo, onClickClose, isStuck }: StickyHeaderProps,
  ref: ForwardedRef<HTMLElement>
): JSX.Element {
  const theme = useTheme();

  // The pseudo element is primarily to prevent scrolling content from being visible above the header on mobile,
  // as bottomsheet padding makes it difficult to do normally while maintaining the sticky header
  const backgroundPseudoElStyle: FlexProps['sx'] = {
    '&::before': {
      content: '""',
      position: 'absolute',
      display: 'block',
      height: '100vh',
      width: ['100vw', '100%'],
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'inherit',
      zIndex: -1,
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: isStuck ? theme.colors.border.primary : 'transparent',
    },
  };

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      position="sticky"
      top="0"
      pt={['1x', '0x']}
      pb={['5x', '6x']}
      background="primary"
      zIndex="10"
      sx={{ ...backgroundPseudoElStyle }}
      htmlRef={ref}
    >
      <Flex gap="2x" alignItems="center">
        <CloseButton display={['flex', 'none']} variant="mobile" onClickClose={onClickClose} />
        <Avatar
          diameter={theme.space['8x']}
          size={Size.Small}
          borderRadius="50%"
          avatarType={AvatarType.Person}
          imageUrl={trainerInfo.defaultImage.small}
        />
        <Text size="200" fontWeight="semibold">
          {trainerInfo.shortName}
        </Text>
      </Flex>
      <CloseButton display={['none', 'flex']} variant="desktop" onClickClose={onClickClose} />
    </Flex>
  );
}
export default forwardRef(StickyHeader);
